// src/pages/dashboards/admin/AdminPartners.jsx
// Referral partners, their consultants, their codes, and what is owed.
//
// Attribution happens once at registration when someone enters a referral code.
// A code resolves to a partner and optionally to one of that partner's
// consultants. Nothing moves afterwards.
//
// Rates are independent: the partner rate is what we pay them, the consultant
// rate is what they have told us they pass on. We pay the partner only.

import React, { useEffect, useState } from "react";
import {
  listPartners, createPartner, updatePartner,
  listConsultants, createConsultant, updateConsultant,
    listPartnerCodes, createPartnerCode, setPartnerCodeActive,
  listPartnerLogins, createPartnerLogin, setPartnerLoginActive,
  getCommission,
} from "../../../api/admin";
import { Card, Input, Select, Button, Badge, Loading, EmptyState, Modal, Field } from "../../../components/ui";
import { Plus, ChevronDown, Users, Ticket, Lock } from "../../../components/icons";
import { money, date } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// 0.2 -> "20%"
const pct = (r) => (r == null ? "\u2014" : `${Math.round(Number(r) * 1000) / 10}%`);
// "20" -> 0.2
const toRate = (s) => parseFloat(s) / 100;

const CODE_HINT = "Letters and numbers, no spaces. People type this at signup.";

// ---------------------------------------------------------------- new partner

function CreatePartnerModal({ onClose, onCreated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", code: "", commission_rate: "20" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createPartner({
        name: f.name.trim(),
        code: f.code.trim(),
        commission_rate: toRate(f.commission_rate),
      });
      toast.success("Partner created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New referral partner">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Partner name">
          <Input value={f.name} onChange={set("name")} required placeholder="Mondtes" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Company code" hint={CODE_HINT}>
            <Input
              value={f.code}
              onChange={(e) => setF((s) => ({ ...s, code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
              required
              placeholder="MONDTES"
              className="font-mono"
            />
          </Field>
          <Field label="Commission" hint="percent of list price">
            <Input type="number" step="0.5" min="0" max="100" value={f.commission_rate} onChange={set("commission_rate")} required />
          </Field>
        </div>
        <p className="text-xs text-tb-muted">
          Consultants and extra codes are added afterwards, from the partner's Manage screen.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Creating..." : "Create partner"}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- manage

function ManagePartnerModal({ partner, onClose, onChanged }) {
  const toast = useToast();
  const [consultants, setConsultants] = useState(null);
  const [codes, setCodes] = useState(null);

    const [logins, setLogins] = useState(null);
  const [addingConsultant, setAddingConsultant] = useState(false);
  const [addingCode, setAddingCode] = useState(false);
  const [addingLogin, setAddingLogin] = useState(false);
  const [lf, setLf] = useState({ name: "", email: "", password: "" });

  const [cf, setCf] = useState({ name: "", email: "", commission_rate: "", code: "" });
  const [kf, setKf] = useState({ code: "", consultant_id: "", label: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    listConsultants(partner.id).then(setConsultants).catch((e) => { toast.error(e.message); setConsultants([]); });
        listPartnerCodes(partner.id).then(setCodes).catch((e) => { toast.error(e.message); setCodes([]); });
    listPartnerLogins(partner.id).then(setLogins).catch((e) => { toast.error(e.message); setLogins([]); });
  };

  // portal logins we create for the partner. two is the working convention,
  // one for day to day and one as a fallback.
  const addLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createPartnerLogin(partner.id, {
        name: lf.name.trim(),
        email: lf.email.trim().toLowerCase(),
        password: lf.password,
      });
      toast.success("Login created");
      setLf({ name: "", email: "", password: "" });
      setAddingLogin(false);
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const toggleLogin = async (l) => {
    try {
      await setPartnerLoginActive(l.id, !l.is_active);
      load();
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partner.id]);

  const addConsultant = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createConsultant(partner.id, {
        name: cf.name.trim(),
        email: cf.email.trim() || undefined,
        commission_rate: cf.commission_rate ? toRate(cf.commission_rate) : undefined,
        code: cf.code.trim() || undefined,
      });
      toast.success("Consultant added");
      setCf({ name: "", email: "", commission_rate: "", code: "" });
      setAddingConsultant(false);
      load();
      onChanged();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const addCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createPartnerCode(partner.id, {
        code: kf.code.trim(),
        consultant_id: kf.consultant_id || undefined,
        label: kf.label.trim() || undefined,
      });
      toast.success("Code created");
      setKf({ code: "", consultant_id: "", label: "" });
      setAddingCode(false);
      load();
      onChanged();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const changeConsultantRate = async (c) => {
    const input = window.prompt(
      `What does ${partner.name} pass on to ${c.name}? Percent of list price.\n\n` +
      `${partner.name} earns ${pct(partner.commission_rate)}.`,
      c.commission_rate != null ? String(Math.round(c.commission_rate * 1000) / 10) : ""
    );
    if (input === null) return;
    const v = parseFloat(input);
    if (isNaN(v) || v < 0 || v > 100) { toast.error("Enter a percentage between 0 and 100."); return; }
    try {
      await updateConsultant(c.id, { commission_rate: v / 100 });
      toast.success("Rate updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleConsultant = async (c) => {
    try {
      await updateConsultant(c.id, { is_active: !c.is_active });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleCode = async (k) => {
    try {
      await setPartnerCodeActive(k.id, !k.is_active);
      toast.success(k.is_active ? "Code closed to new signups" : "Code reopened");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const activeConsultants = (consultants || []).filter((c) => c.is_active);

  return (
    <Modal open onClose={onClose} title={partner.name} width="max-w-3xl">
      <div className="space-y-7">

        {/* rate summary */}
        <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="text-sm">
            <span className="text-tb-muted">We pay {partner.name}</span>{" "}
            <span className="font-semibold">{pct(partner.commission_rate)}</span>{" "}
            <span className="text-tb-muted">of list price</span>
          </span>
          <span className="text-xs text-tb-muted">
            Consultant rates below are what {partner.name} passes on. We settle with {partner.name} only.
          </span>
        </div>

        {/* consultants */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Users size={16} className="text-tb-muted" /> Consultants
            </h3>
            <Button variant="ghost" onClick={() => setAddingConsultant((v) => !v)}>
              {addingConsultant ? "Cancel" : "Add consultant"}
            </Button>
          </div>

          {addingConsultant && (
            <form onSubmit={addConsultant} className="rounded-xl border border-tb-border dark:border-tb-d-border p-4 mb-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name">
                  <Input value={cf.name} onChange={(e) => setCf((s) => ({ ...s, name: e.target.value }))} required placeholder="Jane Dlamini" />
                </Field>
                <Field label="Email" hint="optional">
                  <Input type="email" value={cf.email} onChange={(e) => setCf((s) => ({ ...s, email: e.target.value }))} placeholder="jane@mondtes.co.za" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Their share" hint="percent of list price, optional">
                  <Input type="number" step="0.5" min="0" max="100" value={cf.commission_rate} onChange={(e) => setCf((s) => ({ ...s, commission_rate: e.target.value }))} placeholder="10" />
                </Field>
                <Field label="Their code" hint="optional, can be added later">
                  <Input
                    value={cf.code}
                    onChange={(e) => setCf((s) => ({ ...s, code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                    placeholder="MONDTES-JD"
                    className="font-mono"
                  />
                </Field>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy}>{busy ? "Adding..." : "Add"}</Button>
              </div>
            </form>
          )}

          {consultants === null ? (
            <Loading label="Loading consultants" />
          ) : consultants.length === 0 ? (
            <p className="text-sm text-tb-muted">No consultants. Signups on the company code go to {partner.name} directly.</p>
          ) : (
            <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
              {consultants.map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {c.name}
                      {!c.is_active && <span className="ml-2 text-xs text-tb-muted">(inactive)</span>}
                    </p>
                    <p className="text-xs text-tb-muted truncate">
                      {c.email || "No email"} · {c.codes} code{c.codes !== 1 ? "s" : ""} · {c.signups} signup{c.signups !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <span className="text-sm tabular-nums shrink-0">{pct(c.commission_rate)}</span>
                  <Button variant="ghost" onClick={() => changeConsultantRate(c)}>Rate</Button>
                  <Button variant="ghost" onClick={() => toggleConsultant(c)}>
                    {c.is_active ? "Disable" : "Enable"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* codes */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Ticket size={16} className="text-tb-muted" /> Referral codes
            </h3>
            <Button variant="ghost" onClick={() => setAddingCode((v) => !v)}>
              {addingCode ? "Cancel" : "New code"}
            </Button>
          </div>

          {addingCode && (
            <form onSubmit={addCode} className="rounded-xl border border-tb-border dark:border-tb-d-border p-4 mb-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Code" hint={CODE_HINT}>
                  <Input
                    value={kf.code}
                    onChange={(e) => setKf((s) => ({ ...s, code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                    required
                    placeholder="MONDTES-WINTER"
                    className="font-mono"
                  />
                </Field>
                <Field label="Consultant" hint="leave blank for a company code">
                  <Select value={kf.consultant_id} onChange={(e) => setKf((s) => ({ ...s, consultant_id: e.target.value }))}>
                    <option value="">Company (no consultant)</option>
                    {activeConsultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              </div>
              <Field label="Label" hint="optional, for your own reference">
                <Input value={kf.label} onChange={(e) => setKf((s) => ({ ...s, label: e.target.value }))} placeholder="Winter campaign" />
              </Field>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy}>{busy ? "Creating..." : "Create code"}</Button>
              </div>
            </form>
          )}

          {codes === null ? (
            <Loading label="Loading codes" />
          ) : codes.length === 0 ? (
            <p className="text-sm text-tb-muted">No codes yet.</p>
          ) : (
            <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
              {codes.map((k) => (
                <div key={k.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono font-medium truncate">{k.code}</p>
                    <p className="text-xs text-tb-muted truncate">
                      {k.consultant_name || "Company code"}
                      {k.label ? ` · ${k.label}` : ""} · {k.signups} signup{k.signups !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <Badge tone={k.is_active ? "green" : "gray"}>{k.is_active ? "Active" : "Closed"}</Badge>
                  <Button variant="ghost" onClick={() => toggleCode(k)}>
                    {k.is_active ? "Close" : "Reopen"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

                {/* portal logins */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Lock size={16} className="text-tb-muted" /> Portal logins
            </h3>
            <Button variant="ghost" onClick={() => setAddingLogin((v) => !v)}>
              {addingLogin ? "Cancel" : "New login"}
            </Button>
          </div>

          <p className="text-xs text-tb-muted mb-3">
            {partner.name} signs in at the portal to see their own signups and commission. They see
            counts and money only, never a subscriber's receipts or personal data.
          </p>

          {addingLogin && (
            <form onSubmit={addLogin} className="rounded-xl border border-tb-border dark:border-tb-d-border p-4 mb-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name">
                  <Input value={lf.name} onChange={(e) => setLf((s) => ({ ...s, name: e.target.value }))} required placeholder="Accounts" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={lf.email} onChange={(e) => setLf((s) => ({ ...s, email: e.target.value }))} required placeholder="accounts@mondtes.co.za" />
                </Field>
              </div>
              <Field label="Password" hint="send it to them yourself, it is not emailed">
                <Input type="text" value={lf.password} onChange={(e) => setLf((s) => ({ ...s, password: e.target.value }))} required minLength={8} placeholder="At least 8 characters" />
              </Field>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy}>{busy ? "Creating..." : "Create login"}</Button>
              </div>
            </form>
          )}

          {logins === null ? (
            <Loading label="Loading logins" />
          ) : logins.length === 0 ? (
            <p className="text-sm text-tb-muted">No logins yet. {partner.name} cannot see their dashboard until you create one.</p>
          ) : (
            <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
              {logins.map((l) => (
                <div key={l.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{l.name}</p>
                    <p className="text-xs text-tb-muted truncate">
                      {l.email} · {l.last_login_at ? `last in ${date(l.last_login_at)}` : "never signed in"}
                    </p>
                  </div>
                  <Badge tone={l.is_active ? "green" : "gray"}>{l.is_active ? "Active" : "Disabled"}</Badge>
                  <Button variant="ghost" onClick={() => toggleLogin(l)}>
                    {l.is_active ? "Disable" : "Enable"}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {logins && logins.filter((l) => l.is_active).length > 2 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              More than two active logins. Disable the ones no longer in use.
            </p>
          )}
        </section>

        <div className="flex justify-end pt-1">
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- commission row

function CommissionRow({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-tb-border/60 dark:border-tb-d-border/60">
        <td className="px-4 py-3 font-medium">{p.partner_name}</td>
        <td className="px-4 py-3">{p.users}</td>
        <td className="px-4 py-3">{p.payments.length}</td>
        <td className="px-4 py-3">{money(p.revenue)}</td>
        <td className="px-4 py-3 text-tb-muted">{pct(p.commission_rate)}</td>
        <td className="px-4 py-3 font-semibold">{money(p.commission_due)}</td>
        <td className="px-4 py-3 text-right">
          <Button variant="ghost" onClick={() => setOpen((v) => !v)}>
            <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
          </Button>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-tb-border/60 dark:border-tb-d-border/60">
          <td colSpan={7} className="px-4 py-4 bg-tb-bg/60 dark:bg-tb-d-inset/40 space-y-5">

            {/* consultant split - what the partner settles internally */}
            <div>
              <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">By consultant</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-tb-muted">
                    <th className="py-1.5 font-medium">Consultant</th>
                    <th className="py-1.5 font-medium">Signups</th>
                    <th className="py-1.5 font-medium">Revenue</th>
                    <th className="py-1.5 font-medium text-right">{p.partner_name} earns</th>
                    <th className="py-1.5 font-medium text-right">Passes on</th>
                  </tr>
                </thead>
                <tbody>
                  {p.consultants.map((c) => (
                    <tr key={c.consultant_id || "direct"}>
                      <td className="py-1.5">{c.consultant_name}</td>
                      <td className="py-1.5">{c.users}</td>
                      <td className="py-1.5">{money(c.revenue)}</td>
                      <td className="py-1.5 text-right">{money(c.commission)}</td>
                      <td className="py-1.5 text-right">
                        {c.consultant_share == null
                          ? <span className="text-tb-muted">no rate set</span>
                          : money(c.consultant_share)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* payment lines so a figure can be traced */}
            <div>
              <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Payments</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-tb-muted">
                    <th className="py-1.5 font-medium">Subscriber</th>
                    <th className="py-1.5 font-medium">Code</th>
                    <th className="py-1.5 font-medium">Consultant</th>
                    <th className="py-1.5 font-medium">Tier</th>
                    <th className="py-1.5 font-medium">Paid</th>
                    <th className="py-1.5 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {p.payments.map((x) => (
                    <tr key={x.payment_id}>
                      <td className="py-1.5">
                        {x.user_name}
                        <span className="text-tb-muted"> · {x.user_email}</span>
                        {x.role === "practitioner" && <Badge tone="gray" className="ml-2">practitioner</Badge>}
                      </td>
                      <td className="py-1.5 font-mono">{x.code}</td>
                      <td className="py-1.5">{x.consultant_name || "\u2014"}</td>
                      <td className="py-1.5">{x.tier}</td>
                      <td className="py-1.5 text-tb-muted">{date(x.paid_at)}</td>
                      <td className="py-1.5 text-right">{money(x.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------- page

export default function AdminPartners() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState(null);

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const load = () =>
    listPartners().then(setRows).catch((e) => { toast.error(e.message); setRows([]); });

  const loadReport = () => {
    setLoadingReport(true);
    getCommission({ from, to })
      .then(setReport)
      .catch((e) => { toast.error(e.message); setReport(null); })
      .finally(() => setLoadingReport(false));
  };

  useEffect(() => { load(); loadReport(); /* eslint-disable-next-line */ }, []);

  const changeRate = async (p) => {
    const input = window.prompt(`What do we pay ${p.name}? Percent of list price.`, String(Math.round(p.commission_rate * 1000) / 10));
    if (input === null) return;
    const v = parseFloat(input);
    if (isNaN(v) || v < 0 || v > 100) { toast.error("Enter a percentage between 0 and 100."); return; }
    try {
      await updatePartner(p.id, { commission_rate: v / 100 });
      toast.success("Rate updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleActive = async (p) => {
    try {
      await updatePartner(p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? "Codes closed to new signups" : "Codes reopened");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleTerminated = async (p) => {
    const ending = !p.terminated_at;
    if (ending && !window.confirm(`End the ${p.name} agreement? Commission stops from today.`)) return;
    try {
      await updatePartner(p.id, { terminated: ending });
      toast.success(ending ? "Agreement ended" : "Agreement reinstated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold">Referral partners</h1>
        <Button onClick={() => setCreating(true)}><Plus size={16} /> New partner</Button>
      </div>

      {/* partners */}
      <Card className="mb-8">
        {rows === null ? (
          <Loading label="Loading partners" />
        ) : rows.length === 0 ? (
          <EmptyState title="No partners yet" hint="Create one, then add consultants and codes for them to hand out." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Rate</th>
                  <th className="px-4 py-3 font-medium">Codes</th>
                  <th className="px-4 py-3 font-medium">Consultants</th>
                  <th className="px-4 py-3 font-medium">Signups</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3">{pct(p.commission_rate)}</td>
                    <td className="px-4 py-3">{p.codes}</td>
                    <td className="px-4 py-3">{p.consultants}</td>
                    <td className="px-4 py-3">
                      {p.signups}
                      {p.practitioners > 0 && (
                        <span className="text-xs text-tb-muted"> ({p.practitioners} prac)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.terminated_at ? (
                        <Badge tone="red">Ended {date(p.terminated_at)}</Badge>
                      ) : p.is_active ? (
                        <Badge tone="green">Active</Badge>
                      ) : (
                        <Badge tone="gray">Codes closed</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" onClick={() => setManaging(p)}>Manage</Button>
                      <Button variant="ghost" onClick={() => changeRate(p)}>Rate</Button>
                      {!p.terminated_at && (
                        <Button variant="ghost" onClick={() => toggleActive(p)}>
                          {p.is_active ? "Close" : "Reopen"}
                        </Button>
                      )}
                      <Button variant="ghost" onClick={() => toggleTerminated(p)}>
                        {p.terminated_at ? "Reinstate" : "End"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* commission */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <h2 className="text-lg font-display font-semibold mr-auto">Commission owed</h2>
        <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button onClick={loadReport} disabled={loadingReport}>{loadingReport ? "Loading..." : "Run"}</Button>
      </div>

      <Card>
        {loadingReport ? (
          <Loading label="Calculating" />
        ) : !report || report.partners.length === 0 ? (
          <EmptyState
            title="Nothing owed for this period"
            hint="Commission is counted on payments made by people who entered a referral code at registration."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Subscribers</th>
                  <th className="px-4 py-3 font-medium">Payments</th>
                  <th className="px-4 py-3 font-medium">Revenue</th>
                  <th className="px-4 py-3 font-medium">Rate</th>
                  <th className="px-4 py-3 font-medium">Owed</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {report.partners.map((p) => <CommissionRow key={p.partner_id} p={p} />)}
                <tr className="bg-tb-bg/60 dark:bg-tb-d-inset/40">
                  <td className="px-4 py-3 font-semibold" colSpan={3}>Total</td>
                  <td className="px-4 py-3 font-semibold">{money(report.total_revenue)}</td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 font-semibold">{money(report.total_commission)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-4 text-xs text-tb-muted leading-relaxed max-w-3xl">
        Attribution is set once, when someone enters a referral code at registration, and never
        changes. Re-running a past period always returns the same figures. We settle with the
        partner only &mdash; consultant amounts are shown so the partner can pay their own people.
      </p>

      {creating && <CreatePartnerModal onClose={() => setCreating(false)} onCreated={load} />}
      {managing && (
        <ManagePartnerModal
          partner={managing}
          onClose={() => setManaging(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}